import axios from 'axios';
import { toast } from 'react-toastify';
import { authStorage } from './authStorage';
import { clearSession, createTokenRefresher, decodeJwt } from '../utils/session';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
const api = axios.create({ baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json' }, timeout: 30000, withCredentials: true });
let onSessionExpired = null;
export const registerSessionExpiredHandler = handler => { onSessionExpired = handler; };
const handleSessionExpired = () => {
  clearSession(authStorage);
  if (onSessionExpired) onSessionExpired();
  else window.location.href = '/login?expired=1';
};
export const refreshAccessToken = createTokenRefresher(authStorage, async refreshToken => {
  const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, { refreshToken }, { timeout: 15000, withCredentials: true });
  return response.data.data;
});
const isPublicRequest = config => (config.url || '').startsWith('/api/public/') || config.url === '/api/system-settings/public';
const isAuthRequest = config => ['/api/auth/login', '/api/auth/refresh', '/api/auth/logout'].includes(config.url);
api.interceptors.request.use(config => {
  const token = authStorage.getItem('accessToken');
  const publicReadWithExpiredToken = isPublicRequest(config) && config.method === 'get' && (decodeJwt(token)?.exp || 0) * 1000 <= Date.now() + 5000;
  if (token && !publicReadWithExpiredToken && !isAuthRequest(config)) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(response => response, async error => {
  const original = error.config || {};
  const status = error.response?.status;
  if (isAuthRequest(original)) return Promise.reject(error);
  if (status === 503) {
    toast.warning(error.response?.data?.message || 'Hệ thống đang bảo trì, vui lòng quay lại sau.', { toastId: 'maintenance-503' });
    return Promise.reject(error);
  }
  if (status === 401 && (!isPublicRequest(original) || original.method !== 'get') && authStorage.getItem('accessToken')) {
    if (original._retry) { handleSessionExpired(); return Promise.reject(error); }
    original._retry = true;
    try {
      const { accessToken } = await refreshAccessToken();
      original.headers.Authorization = `Bearer ${accessToken}`;
      return api(original);
    } catch (refreshError) {
      // A network outage or server failure must not erase a valid browser session.
      if (!refreshError.sessionChanged && (refreshError.invalidSession || [401, 403].includes(refreshError.response?.status))) handleSessionExpired();
      else if (!refreshError.sessionChanged) toast.info('Chưa thể kết nối để làm mới phiên. Vui lòng thử lại.', { toastId: 'session-reconnect' });
      return Promise.reject(refreshError);
    }
  }
  if (status !== 401) {
    const message = error.response?.data?.message || error.message || 'Đã xảy ra lỗi';
    toast.error(message, { toastId: `api-error:${original.method || ''}:${original.url || ''}:${message}` });
  }
  return Promise.reject(error);
});
export const publicApi = axios.create({ baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json' }, timeout: 30000 });
export default api;
export { API_BASE_URL };
